/**
 * FlowRunHistory - Run history list for a specific flow
 * Shows status, duration, relative time, and expandable error messages
 */

import { useState, useEffect } from 'react';
import { AlertCircle, CheckCircle2, Clock, Loader2, ShieldX } from 'lucide-react';
import { useFlowRuns } from '@/hooks/use-flows';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { testIds } from '@/lib/test-ids';
import type { FlowRun } from '@/lib/api';

interface FlowRunHistoryProps {
  flowId: string;
  /** Increment after a manual run so the list refetches (parent-owned counter). */
  refreshVersion?: number;
}

/** Format duration between started and completed timestamps as human-readable string */
function formatDuration(startedAt: string, completedAt: string | null): string {
  if (!completedAt) return '—';
  
  const start = new Date(startedAt).getTime();
  const end = new Date(completedAt).getTime();
  const diffMs = end - start;
  
  const seconds = Math.floor(diffMs / 1000);
  const minutes = Math.floor(seconds / 60);
  
  if (minutes > 0) {
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  }
  
  return `${seconds}s`;
}

/** Format timestamp as relative time (e.g. "2h ago") */
function formatRelativeTime(timestamp: string): string {
  const now = Date.now();
  const time = new Date(timestamp).getTime();
  const diffMs = now - time;
  
  const seconds = Math.floor(diffMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  
  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (minutes > 0) return `${minutes}m ago`;
  return `${seconds}s ago`;
}

/** Render status badge with appropriate color and icon for flow run status */
export function RunStatusBadge({ status }: { status: FlowRun['status'] }) {
  const variants = {
    completed: {
      icon: CheckCircle2,
      className: 'bg-green-500/10 text-green-500 border-green-500/20',
      label: 'Completed',
    },
    failed: {
      icon: AlertCircle,
      className: 'bg-red-500/10 text-red-500 border-red-500/20',
      label: 'Failed',
    },
    running: {
      icon: Loader2,
      className: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
      label: 'Running',
    },
    policy_blocked: {
      icon: ShieldX,
      className: 'bg-orange-500/10 text-orange-500 border-orange-500/20',
      label: 'Blocked',
    },
    pending: {
      icon: Clock,
      className: 'bg-gray-500/10 text-gray-500 border-gray-500/20',
      label: 'Pending',
    },
  };

  const variant = variants[status];
  const Icon = variant.icon;

  return (
    <Badge className={cn('border text-xs font-medium px-2 py-0.5 shrink-0', variant.className)}>
      <Icon className={cn('w-3.5 h-3.5 mr-1 shrink-0', status === 'running' && 'animate-spin')} />
      {variant.label}
    </Badge>
  );
}

/** Render a single flow run row with status, duration, and expandable error message */
function RunRow({ run }: { run: FlowRun }) {
  const [errorExpanded, setErrorExpanded] = useState(false);
  const hasTruncatedError = run.error && run.error.length > 80;

  return (
    <div 
      className="border-b border-sidebar-border last:border-0 py-2 px-3 space-y-1"
      data-testid={testIds.flowsManager.runRow(run.id)}
    >
      <div className="flex items-center justify-between gap-2">
        <RunStatusBadge status={run.status} />
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>{formatDuration(run.started_at, run.completed_at)}</span>
          <span>•</span>
          <span>{formatRelativeTime(run.started_at)}</span>
        </div>
      </div>
      
      {run.trigger_type && (
        <div className="text-xs text-muted-foreground">
          Trigger: {run.trigger_type}
        </div>
      )}
      
      {run.error && (
        <div className="mt-2">
          <div
            className={cn(
              'text-xs text-red-500 bg-red-500/5 border border-red-500/20 rounded px-2 py-1',
              !errorExpanded && hasTruncatedError && 'cursor-pointer hover:bg-red-500/10'
            )}
            onClick={() => hasTruncatedError && setErrorExpanded(!errorExpanded)}
          >
            {errorExpanded || !hasTruncatedError
              ? run.error
              : `${run.error.slice(0, 80)}...`}
          </div>
        </div>
      )}
    </div>
  );
}

/** Render loading skeleton for run history */
function LoadingSkeleton() {
  return (
    <div className="space-y-2 p-3">
      {[1, 2, 3].map((i) => (
        <div key={i} className="space-y-2">
          <Skeleton className="h-6 w-24" />
          <Skeleton className="h-4 w-32" />
        </div>
      ))}
    </div>
  );
}

/** Render empty state when no runs exist */
function EmptyState() {
  return (
    <div className="text-xs text-muted-foreground text-center py-6 px-3">
      No runs yet - enable this flow or click Run now
    </div>
  );
}

/** Display run history list for a specific flow with status, duration, and errors */
export function FlowRunHistory({ flowId, refreshVersion = 0 }: FlowRunHistoryProps) {
  const { runs, loading, error, refetch } = useFlowRuns(flowId);

  useEffect(() => {
    if (refreshVersion > 0) {
      void refetch();
    }
  }, [refreshVersion, refetch]);

  if (loading) {
    return <LoadingSkeleton />;
  }

  if (error) {
    return (
      <div className="text-xs text-red-500 text-center py-6 px-3">
        Failed to load run history
      </div>
    );
  }

  if (runs.length === 0) {
    return <EmptyState />;
  }

  return (
    <div className="border-t border-sidebar-border bg-sidebar/50">
      {runs.map((run) => (
        <RunRow key={run.id} run={run} />
      ))}
    </div>
  );
}
